import { useState, useMemo } from 'react'
import { useScheduledPosts } from '@/api/hooks/usePoster'
import { useAccounts } from '@/api/hooks/useAccounts'
import { useLanguage } from '@/i18n/LanguageContext'
import { AlertTriangle, ShieldAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PageWarning {
  pageId: string
  name: string
  daysRemaining: number
  pendingCount: number
}

interface TokenWarning {
  name: string
  type: 'expired' | 'expiring'
  daysLeft?: number
}

export function useTokenWarnings() {
  const { data: accounts } = useAccounts()

  return useMemo<TokenWarning[]>(() => {
    if (!accounts) return []
    const warnings: TokenWarning[] = []
    const now = Date.now()

    for (const acct of accounts) {
      // Check if ALL tokens are invalid
      if (acct.tokens && acct.tokens.length > 0 && acct.tokens.every(t => !t.valid)) {
        warnings.push({ name: acct.name || acct.username, type: 'expired' })
        continue
      }

      // Check if tokenExpiry is within 7 days
      if (acct.tokenExpiry) {
        const expiryMs = new Date(acct.tokenExpiry).getTime()
        const daysLeft = Math.round((expiryMs - now) / 86_400_000)
        if (daysLeft <= 7 && daysLeft > 0) {
          warnings.push({ name: acct.name || acct.username, type: 'expiring', daysLeft })
        } else if (daysLeft <= 0) {
          // Only add if not already added from tokens check
          if (!warnings.some(w => w.name === (acct.name || acct.username))) {
            warnings.push({ name: acct.name || acct.username, type: 'expired' })
          }
        }
      }
    }

    // Sort: expired first, then expiring by days
    return warnings.sort((a, b) => {
      if (a.type === 'expired' && b.type !== 'expired') return -1
      if (a.type !== 'expired' && b.type === 'expired') return 1
      return (a.daysLeft ?? 0) - (b.daysLeft ?? 0)
    })
  }, [accounts])
}

export function ScheduleAlert() {
  const { t } = useLanguage()
  const { data: posts } = useScheduledPosts()
  const tokenWarnings = useTokenWarnings()
  const [dismissedSchedule, setDismissedSchedule] = useState(false)
  const [dismissedTokens, setDismissedTokens] = useState(false)

  const warnings = useMemo<PageWarning[]>(() => {
    if (!posts) return []
    const now = Date.now()
    const pending = posts.filter(
      p => !p.published && p.status !== 'published' && p.status !== 'failed',
    )

    const todayStr = new Date(now).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })
    const todayMs = new Date(todayStr + 'T00:00:00').getTime()

    const byPage = new Map<string, { name: string; maxAt: number; count: number }>()
    for (const p of pending) {
      const key = p.pageId
      const existing = byPage.get(key)
      if (!existing) {
        byPage.set(key, { name: p.accountName || key, maxAt: p.scheduledAt, count: 1 })
      } else {
        if (p.scheduledAt > existing.maxAt) existing.maxAt = p.scheduledAt
        existing.count++
      }
    }

    const result: PageWarning[] = []
    for (const [pageId, info] of byPage) {
      const maxStr = new Date(info.maxAt).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })
      const maxMs = new Date(maxStr + 'T00:00:00').getTime()
      const days = Math.round((maxMs - todayMs) / 86_400_000)
      if (days <= 7) {
        result.push({ pageId, name: info.name, daysRemaining: Math.max(0, days), pendingCount: info.count })
      }
    }
    return result.sort((a, b) => a.daysRemaining - b.daysRemaining)
  }, [posts])

  const showScheduleAlert = !dismissedSchedule && warnings.length > 0

  if (!showScheduleAlert) return null

  return (
    <>
      {/* Schedule running low alerts */}
      {showScheduleAlert && (
        <div className="border-b border-border bg-amber-500/10 px-4 py-2">
          <div className="mx-auto flex max-w-[1100px] items-start justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              {warnings.map(w => (
                <div key={w.pageId} className="flex items-center gap-2 text-xs">
                  <AlertTriangle
                    className={cn(
                      'h-3.5 w-3.5 shrink-0',
                      w.daysRemaining <= 2 ? 'text-destructive' : 'text-amber-500',
                    )}
                  />
                  <span className={cn(
                    'font-medium',
                    w.daysRemaining <= 2 ? 'text-destructive' : 'text-amber-600 dark:text-amber-400',
                  )}>
                    {w.name}:
                  </span>
                  <span className={cn(
                    w.daysRemaining <= 2 ? 'text-destructive/80' : 'text-amber-600/80 dark:text-amber-400/80',
                  )}>
                    {w.daysRemaining === 0
                      ? t('acaba hoje')
                      : `${w.daysRemaining} ${w.daysRemaining === 1 ? t('dia restante') : t('dias restantes')}`}
                    {' '}({w.pendingCount} {w.pendingCount === 1 ? 'post' : 'posts'})
                  </span>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setDismissedSchedule(true)}
              className="mt-0.5 shrink-0 rounded p-0.5 text-amber-600 transition-colors hover:bg-amber-500/20 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  )
}
